import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { toggleVariants } from '@/components/ui/toggle';
import { cn } from '@/lib/utils';

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & {
    spacing?: number;
    orientation?: 'horizontal' | 'vertical';
    shape?: 'rounded' | 'round';
  }
>({
  size: 'default',
  variant: 'default',
  spacing: 0,
  orientation: 'horizontal',
  shape: 'rounded',
});

export type ToggleGroupProps = ToggleGroupPrimitive.Props &
  VariantProps<typeof toggleVariants> & {
    spacing?: number;
    orientation?: 'horizontal' | 'vertical';
    shape?: 'rounded' | 'round';
  };

function ToggleGroup({
  className,
  variant,
  size,
  spacing = 0,
  orientation = 'horizontal',
  shape = 'rounded',
  children,
  ...props
}: ToggleGroupProps) {
  const groupStyle = React.useMemo(
    () => ({ '--gap': spacing }) as React.CSSProperties,
    [spacing],
  );
  const contextValue = React.useMemo(
    () => ({ variant, size, spacing, orientation, shape }),
    [orientation, shape, size, spacing, variant],
  );

  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      data-orientation={orientation}
      orientation={orientation}
      data-shape={shape}
      // base-ui 1.4.0 adds aria-orientation but it's invalid on role="group" (axe aria-allowed-attr)
      aria-orientation={undefined}
      style={groupStyle}
      className={cn(
        'group/toggle-group flex w-fit flex-row items-center gap-[--spacing(var(--gap))] data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch',
        shape === 'round' ? 'rounded-full' : 'rounded-lg',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={contextValue}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
}

export type ToggleGroupItemProps = TogglePrimitive.Props &
  VariantProps<typeof toggleVariants>;

function ToggleGroupItem({
  className,
  children,
  variant = 'default',
  size = 'default',
  ...props
}: ToggleGroupItemProps) {
  const context = React.useContext(ToggleGroupContext);
  const connectedShapeClassName =
    context.orientation === 'vertical'
      ? context.shape === 'round'
        ? 'group-data-[shape=round]/toggle-group:data-[spacing=0]:first:rounded-t-full group-data-[shape=round]/toggle-group:data-[spacing=0]:last:rounded-b-full'
        : 'group-data-[shape=rounded]/toggle-group:data-[spacing=0]:first:rounded-t-lg group-data-[shape=rounded]/toggle-group:data-[spacing=0]:last:rounded-b-lg'
      : context.shape === 'round'
        ? 'group-data-[shape=round]/toggle-group:data-[spacing=0]:first:rounded-s-full group-data-[shape=round]/toggle-group:data-[spacing=0]:last:rounded-e-full'
        : 'group-data-[shape=rounded]/toggle-group:data-[spacing=0]:first:rounded-s-lg group-data-[shape=rounded]/toggle-group:data-[spacing=0]:last:rounded-e-lg';

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      data-spacing={context.spacing}
      className={cn(
        'data-[state=on]:bg-accent group-data-[spacing=0]/toggle-group:rounded-none group-data-[spacing=0]/toggle-group:shadow-none shrink-0 focus:z-10 focus-visible:z-10 group-data-[orientation=horizontal]/toggle-group:data-[spacing=0]:data-[variant=outline]:border-s-0 group-data-[orientation=vertical]/toggle-group:data-[spacing=0]:data-[variant=outline]:border-t-0 group-data-[orientation=horizontal]/toggle-group:data-[spacing=0]:data-[variant=outline]:first:border-s group-data-[orientation=vertical]/toggle-group:data-[spacing=0]:data-[variant=outline]:first:border-t',
        context.shape === 'round' && 'not-data-[spacing=0]:rounded-full',
        connectedShapeClassName,
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
}

ToggleGroup.displayName = 'ToggleGroup';
ToggleGroupItem.displayName = 'ToggleGroupItem';

export { ToggleGroup, ToggleGroupItem };
